import axios from 'axios';
import config from '../config/env';

// Get base URL - in production use full API URL, in development use relative path for proxy
const getBaseURL = () => {
  // If API_BASE_URL is configured (production), use it
  if (config.API_BASE_URL && !config.API_BASE_URL.includes('localhost')) {
    return config.API_BASE_URL;
  }
  // Otherwise use relative path (development with webpack proxy)
  return '/api';
};

// Create axios instance
const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token and language to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add Accept-Language header based on user's language preference
    const language = localStorage.getItem('animap_language') || 'el';
    config.headers['Accept-Language'] = language;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Skip token refresh for auth endpoints (login, register, etc.)
    const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                          originalRequest.url?.includes('/auth/register') ||
                          originalRequest.url?.includes('/auth/email/') ||
                          originalRequest.url?.includes('/auth/password/') ||
                          originalRequest.url?.includes('/auth/2fa/');

    // If error is 401 and we haven't tried to refresh yet (skip for auth endpoints)
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        // Try to refresh the token
        const refreshURL = `${getBaseURL()}/auth/token/refresh/`;
        const response = await axios.post(refreshURL, {
          refresh: refreshToken,
        });

        const { access, refresh } = response.data;

        // Update tokens in localStorage
        localStorage.setItem('accessToken', access);
        if (refresh) {
          localStorage.setItem('refreshToken', refresh);
        }

        // Update the failed request with new token
        originalRequest.headers.Authorization = `Bearer ${access}`;

        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear auth and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
